// src/features/estoque/components/ListaEstoque.jsx
import React, { useState, useEffect } from "react";
import { 
  observarEstoque, 
  fecharTemporadaEstoque, 
  obterTemporadasFechadas,
  reabrirTemporadaEstoque,
  ajustarSaldoManualEstoque
} from "services/estoqueService";
import { useUser } from "hooks/useUser";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Search, PackageOpen, Archive, Lock, Edit2, Sparkles, Check } from "lucide-react";
import toast from "react-hot-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";

/**
 * Componente que exibe a lista de estoque atualizada em tempo real (onSnapshot).
 */
const ListaEstoque = () => {
  const { isAdmin } = useUser();
  const [estoqueAtual, setEstoqueAtual] = useState([]);
  const [itensExibidos, setItensExibidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  
  // Temporadas
  const [temporadas, setTemporadas] = useState([]);
  const [temporadaSelecionada, setTemporadaSelecionada] = useState("atual");
  
  // Modal de Fechamento
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nomeTemporada, setNomeTemporada] = useState("");
  const [isClosing, setIsClosing] = useState(false);

  // Modal de Reabertura
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [isReopening, setIsReopening] = useState(false);

  // Modal de Ajuste Manual de Saldo (Admin)
  const [itemParaAjuste, setItemParaAjuste] = useState(null);
  const [novaQtdAjuste, setNovaQtdAjuste] = useState("");
  const [ignorarEstoqueAjuste, setIgnorarEstoqueAjuste] = useState(false);
  const [motivoAjuste, setMotivoAjuste] = useState("Correção de inventário da temporada atual");
  const [isSalvandoAjuste, setIsSalvandoAjuste] = useState(false);

  const formatQuantity = (qty) => {
    const num = Number(qty);
    return isNaN(num) ? "0" : num.toFixed(0);
  };

  // Carregar histórico de temporadas
  const carregarTemporadas = async () => {
    const historico = await obterTemporadasFechadas();
    setTemporadas(historico);
  };

  useEffect(() => {
    carregarTemporadas();
    
    // Escuta em tempo real o estoque atual
    const unsubscribe = observarEstoque((dadosAtualizados) => {
      setEstoqueAtual(dadosAtualizados);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Efeito para alternar os itens exibidos dependendo da temporada selecionada
  useEffect(() => {
    if (temporadaSelecionada === "atual") {
      setItensExibidos(estoqueAtual);
    } else {
      const temp = temporadas.find(t => t.id === temporadaSelecionada);
      if (temp) {
        setItensExibidos(temp.itens || []);
      }
    }
  }, [temporadaSelecionada, estoqueAtual, temporadas]);

  const handleFecharTemporada = async (e) => {
    e.preventDefault();
    if (!nomeTemporada.trim()) {
      toast.error("O nome da temporada é obrigatório.");
      return;
    }
    
    setIsClosing(true);
    const result = await fecharTemporadaEstoque(nomeTemporada.trim());
    setIsClosing(false);
    
    if (result.success) {
      toast.success(result.message);
      setIsModalOpen(false);
      setNomeTemporada("");
      carregarTemporadas(); // Atualiza o dropdown
      setTemporadaSelecionada("atual");
    } else {
      toast.error(result.message);
    }
  };

  const handleReabrirTemporada = async (e) => {
    e.preventDefault();
    setIsReopening(true);
    const result = await reabrirTemporadaEstoque(temporadaSelecionada);
    setIsReopening(false);
    
    if (result.success) {
      toast.success(result.message);
      setIsReopenModalOpen(false);
      carregarTemporadas(); // Atualiza o dropdown
      setTemporadaSelecionada("atual");
    } else {
      toast.error(result.message);
    }
  };

  // Salvar ajuste manual de saldo
  const handleSalvarAjusteManual = async (e) => {
    e.preventDefault();
    if (novaQtdAjuste === "" || isNaN(Number(novaQtdAjuste))) {
      toast.error("Informe uma quantidade válida.");
      return;
    }

    setIsSalvandoAjuste(true);
    const res = await ajustarSaldoManualEstoque(
      itemParaAjuste.codigo || itemParaAjuste.id,
      Number(novaQtdAjuste),
      motivoAjuste,
      ignorarEstoqueAjuste
    );
    setIsSalvandoAjuste(false);

    if (res.success) {
      toast.success(`Saldo de ${itemParaAjuste.nome} corrigido para ${novaQtdAjuste} un!`, { icon: "✅" });
      setItemParaAjuste(null);
    } else {
      toast.error(`Erro: ${res.message}`);
    }
  };

  const estoqueFiltrado = itensExibidos.filter((item) =>
    item.nome.toLowerCase().includes(filtro.toLowerCase()) || 
    (item.codigo && item.codigo.toLowerCase().includes(filtro.toLowerCase()))
  );

  const isHistorico = temporadaSelecionada !== "atual";

  const getStockClasses = (qty, isHist) => {
    if (isHist) return 'bg-gray-100 text-gray-800 dark:bg-accent dark:text-gray-200';
    if (qty < 0) return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400 border border-red-500/50';
    if (qty <= 10) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400';
    return 'bg-gray-100 text-gray-800 dark:bg-accent dark:text-gray-200';
  };

  return (
    <div className="bg-transparent md:bg-white md:dark:bg-card md:shadow-sm md:rounded-lg md:border md:border-gray-200 md:dark:border-border p-1 md:p-6 flex flex-col mt-2 md:mt-4">
      
      {/* Cabeçalho e Controles */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between mb-5 gap-4">
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            {isHistorico ? "Histórico de Estoque" : "Estoque Atual"}
        </h2>
        
        <div className="flex flex-col md:flex-row gap-3 items-center w-full xl:w-auto">
          {/* Select de Temporada */}
          <div className="w-full md:w-56">
            <Select value={temporadaSelecionada} onValueChange={setTemporadaSelecionada}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a Temporada" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="atual" className="font-bold text-emerald-600 dark:text-emerald-400">
                  Ao Vivo (Temporada Atual)
                </SelectItem>
                {temporadas.map((temp) => (
                  <SelectItem key={temp.id} value={temp.id}>
                    {temp.nome || `Fechamento ${new Date(temp.dataFechamento?.toDate ? temp.dataFechamento.toDate() : temp.dataFechamento).toLocaleDateString("pt-BR")}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Input de Busca */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Buscar por código ou nome..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="pl-9 w-full"
            />
          </div>

          {/* Botão de Fechar Temporada (Apenas Admin e na temporada atual) */}
          {isAdmin && !isHistorico && (
            <Button 
              variant="outline" 
              className="w-full md:w-auto text-orange-600 border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-orange-800 dark:text-orange-400 dark:hover:bg-accent"
              onClick={() => setIsModalOpen(true)}
            >
              <Archive className="w-4 h-4 mr-2" />
              Zerar / Nova Temporada
            </Button>
          )}

          {/* Botão de Reabrir Temporada (Apenas Admin e em histórico) */}
          {isAdmin && isHistorico && (
            <Button 
              variant="outline" 
              className="w-full md:w-auto text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-400 dark:hover:bg-accent"
              onClick={() => setIsReopenModalOpen(true)}
            >
              <PackageOpen className="w-4 h-4 mr-2" />
              Desfazer Fechamento
            </Button>
          )}
        </div>
      </div>

      {loading && temporadaSelecionada === "atual" ? (
        <div className="space-y-4 animate-pulse">
            <div className="h-10 bg-gray-200 dark:bg-secondary rounded w-full"></div>
            <div className="h-10 bg-gray-200 dark:bg-secondary rounded w-full"></div>
            <div className="h-10 bg-gray-200 dark:bg-secondary rounded w-full"></div>
        </div>
      ) : estoqueFiltrado.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-border rounded-lg">
            Nenhum produto encontrado.
        </div>
      ) : (
        <>
          {/* Visão Mobile (Cards) */}
          <div className="md:hidden space-y-3">
            {estoqueFiltrado.map((item) => (
              <div key={item.id} className="bg-white dark:bg-card border border-gray-200 dark:border-border rounded-xl p-3.5 shadow-sm flex flex-col gap-2.5">
                <div className="flex justify-between items-start gap-2">
                  <div className="overflow-hidden">
                    <p className="font-bold text-base text-gray-900 dark:text-gray-100 truncate">{item.nome}</p>
                    <p className="text-xs font-mono text-gray-500">Cód: {item.codigo}</p>
                  </div>
                  <div className="shrink-0 text-right flex items-center gap-1.5">
                    <span className={`px-2.5 py-1 rounded-md font-bold text-sm ${getStockClasses(item.quantidadeAtual, isHistorico)}`}>
                      {formatQuantity(item.quantidadeAtual)}
                    </span>
                    {!isHistorico && isAdmin && (
                      <button
                        onClick={() => {
                          setItemParaAjuste(item);
                          setNovaQtdAjuste(formatQuantity(item.quantidadeAtual));
                          setIgnorarEstoqueAjuste(!!item.ignorarEstoque);
                        }}
                        className="p-1 rounded text-slate-400 hover:text-emerald-500 hover:bg-emerald-500/10 transition-colors"
                        title="Ajustar saldo deste produto"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs pt-2.5 border-t border-gray-100 dark:border-border text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <span className="font-medium">NCM:</span>
                    {item.ncm}
                  </div>
                  {!isHistorico && (
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Atualizado:</span>
                      {item.ultimaAtualizacao}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Visão Desktop (Tabela) */}
          <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200 dark:border-border">
            <table className="w-full text-sm text-left text-gray-600 dark:text-gray-300">
              <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-slate-900 border-b dark:border-slate-800">
                <tr>
                  <th scope="col" className="px-6 py-3.5 font-bold">Código</th>
                  <th scope="col" className="px-6 py-3.5 font-bold">Produto</th>
                  <th scope="col" className="px-6 py-3.5 font-bold text-center">NCM</th>
                  <th scope="col" className="px-6 py-3.5 font-bold text-right">Qtd. {isHistorico ? "Final" : "Atual"}</th>
                  {!isHistorico && <th scope="col" className="px-6 py-3.5 font-bold text-right">Última Atualização</th>}
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-800">
                {estoqueFiltrado.map((item) => (
                  <tr 
                      key={item.id} 
                      className="bg-white dark:bg-slate-950/40 hover:bg-gray-50 dark:hover:bg-slate-900/60 transition-colors"
                  >
                    <td className="px-6 py-4 font-mono text-xs font-semibold">{item.codigo}</td>
                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {item.nome}
                    </td>
                    <td className="px-6 py-4 font-mono text-center text-xs">
                      {item.ncm}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center justify-end gap-1.5">
                        <span className={`px-2.5 py-1 rounded-md font-bold font-mono text-sm ${getStockClasses(item.quantidadeAtual, isHistorico)}`}>
                            {formatQuantity(item.quantidadeAtual)}
                        </span>
                        {!isHistorico && isAdmin && (
                          <button
                            onClick={() => {
                              setItemParaAjuste(item);
                              setNovaQtdAjuste(formatQuantity(item.quantidadeAtual));
                              setIgnorarEstoqueAjuste(!!item.ignorarEstoque);
                            }}
                            className="p-1 rounded text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                            title="Ajustar saldo deste produto"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                    {!isHistorico && (
                      <td className="px-6 py-4 text-right text-xs text-gray-400 font-mono">
                        {item.ultimaAtualizacao}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal de Ajuste Manual de Saldo Físico (Admin) */}
      <Dialog open={!!itemParaAjuste} onOpenChange={(open) => !open && setItemParaAjuste(null)}>
        <DialogContent className="max-w-md bg-slate-900 text-slate-100 border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold text-slate-100">
              <Edit2 className="w-4 h-4 text-emerald-400" />
              Ajustar Saldo Físico do Produto
            </DialogTitle>
          </DialogHeader>

          {itemParaAjuste && (
            <form onSubmit={handleSalvarAjusteManual} className="space-y-4 pt-2 text-xs">
              <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 space-y-1">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
                  Produto Selecionado
                </span>
                <p className="font-bold text-slate-200 text-sm">{itemParaAjuste.nome}</p>
                <p className="text-slate-400 font-mono">Código Interno: {itemParaAjuste.codigo}</p>
                <p className="text-slate-400">
                  Saldo Registrado Atual: <strong className="text-amber-400 font-mono">{formatQuantity(itemParaAjuste.quantidadeAtual)} un</strong>
                </p>
              </div>

              {/* Sugestões Inteligentes Calculadas por Produto */}
              {itemParaAjuste.codigo === "00000005" && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-emerald-300">
                        Saldo Calculado para a Temporada Atual:
                      </p>
                      <p className="text-[11px] text-emerald-200/80 mt-0.5">
                        Considerando apenas as entradas desde o fechamento da temporada (11/08/2026):
                        <br />• 1.440 un (Monte Claro: 1.200 s/ gás + 240 c/ gás em fardos c/ 12)
                        <br />• 48 un (Solar Coca-Cola: 24 s/ gás + 24 c/ gás avulsas)
                        <br /><strong>Total Real: 1.488 unidades</strong>
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setNovaQtdAjuste(1488)}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs h-8"
                  >
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    Definir para 1.488 unidades
                  </Button>
                </div>
              )}

              {itemParaAjuste.codigo === "00000006" && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-emerald-300">
                        Saldo Calculado das Notas Fiscais (Temporada Atual):
                      </p>
                      <p className="text-[11px] text-emerald-200/80 mt-0.5 leading-relaxed">
                        • <strong>200 un</strong> (Guaraná Antarctica 1L: 100 packs c/ 2 garrafas)
                        <br />• <strong>120 un</strong> (Pepsi 1L: 10 caixas c/ 12 garrafas)
                        <br />• <strong>36 un</strong> (Sukita 1L: 3 caixas c/ 12 garrafas)
                        <br />• <strong>24 un</strong> (Soda Limonada 1L: 2 caixas c/ 12 garrafas)
                        <br /><strong>Total Real: 380 garrafas</strong> <span className="text-slate-400">(entrou como 115 fardos)</span>
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setNovaQtdAjuste(380)}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs h-8"
                  >
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    Definir para 380 unidades (Garrafas 1L)
                  </Button>
                </div>
              )}

              {itemParaAjuste.codigo === "00000002" && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-emerald-300">
                        Saldo Calculado das Notas Fiscais (Temporada Atual):
                      </p>
                      <p className="text-[11px] text-emerald-200/80 mt-0.5 leading-relaxed">
                        • <strong>384 un</strong> (Solar Coca-Cola: 180 Coca + 120 Zero + 48 Sprite + 36 Fanta já faturadas avulsas)
                        <br />• <strong>120 un</strong> (Ambev: 6 cx Guaraná + 2 cx Soda + 2 cx Sukita = 10 caixas c/ 12)
                        <br /><strong>Total Real: 504 latas</strong> <span className="text-slate-400">(entrou como 394 un)</span>
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setNovaQtdAjuste(504)}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs h-8"
                  >
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    Definir para 504 unidades (Latas)
                  </Button>
                </div>
              )}

              {itemParaAjuste.codigo === "00000003" && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-emerald-300">
                        Saldo Calculado das Notas Fiscais (Temporada Atual):
                      </p>
                      <p className="text-[11px] text-emerald-200/80 mt-0.5 leading-relaxed">
                        • <strong>732 un</strong> (Heyder Cury: 27 cx Heineken + 33 cx Amstel + 1 cx Ultra em caixas c/ 12)
                        <br />• <strong>404 un</strong> (Ambev: 10 cx Skol c/12 + 10 cx Brahma c/12 + 6 cx Budweiser c/12 + 1 cx Spaten c/12 + 3 cx Stella c/8 + 3 cx Corona c/8 + 2 cx Michelob c/8 + 2 cx Stella Gold c/8)
                        <br /><strong>Total Real: 1.136 latas</strong> <span className="text-slate-400">(entrou como 775)</span>
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setNovaQtdAjuste(1136)}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs h-8"
                  >
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    Definir para 1.136 unidades (Cervejas em Lata)
                  </Button>
                </div>
              )}

              {itemParaAjuste.codigo === "00000004" && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-emerald-300">
                        Saldo das Notas Importadas (Temporada Atual):
                      </p>
                      <p className="text-[11px] text-emerald-200/80 mt-0.5 leading-relaxed">
                        • <strong>12 un</strong> (Heyder Cury - NF 218381: 1 caixa de Heineken 0.0% c/ 12 latas)
                        <br /><strong>Total Real Importado: 12 unidades</strong>
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setNovaQtdAjuste(12)}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs h-8"
                  >
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    Definir para 12 unidades (Heineken 0.0)
                  </Button>
                </div>
              )}

              <div>
                <Label htmlFor="novoSaldo" className="text-xs text-slate-300 block mb-1.5 font-semibold">
                  Novo Saldo Físico (unidades no bar)
                </Label>
                <Input
                  id="novoSaldo"
                  type="number"
                  value={novaQtdAjuste}
                  onChange={(e) => setNovaQtdAjuste(e.target.value)}
                  className="bg-slate-950 border-slate-700 text-emerald-400 font-bold font-mono text-base h-10"
                  required
                  autoFocus
                />
              </div>

              <div className="flex items-center gap-2 mt-2 p-2 bg-slate-900 border border-slate-800 rounded-md">
                <input 
                  type="checkbox" 
                  id="ignorarEstoque" 
                  checked={ignorarEstoqueAjuste}
                  onChange={(e) => setIgnorarEstoqueAjuste(e.target.checked)}
                  className="w-4 h-4 text-emerald-500 rounded border-slate-700 bg-slate-800 focus:ring-emerald-500 focus:ring-offset-slate-900"
                />
                <Label htmlFor="ignorarEstoque" className="text-xs text-slate-300 font-medium cursor-pointer">
                  Não controlar estoque (Ideal para Serviços/Pratos)
                </Label>
              </div>

              <div>
                <Label htmlFor="motivoAjuste" className="text-[11px] text-slate-400 block mb-1">
                  Motivo do Ajuste (Opcional)
                </Label>
                <Input
                  id="motivoAjuste"
                  value={motivoAjuste}
                  onChange={(e) => setMotivoAjuste(e.target.value)}
                  className="bg-slate-950 border-slate-800 text-xs h-8 text-slate-300"
                />
              </div>

              <DialogFooter className="pt-2 gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setItemParaAjuste(null)}
                  disabled={isSalvandoAjuste}
                  className="text-slate-400 hover:text-slate-200 text-xs"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isSalvandoAjuste}
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs"
                >
                  {isSalvandoAjuste ? "Salvando..." : "Salvar Saldo"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Fechamento de Temporada */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent aria-describedby={undefined} className="w-full h-[100dvh] max-h-none sm:h-auto sm:max-h-[85vh] sm:max-w-md p-0 border-0 sm:border rounded-none sm:rounded-lg overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b shrink-0 bg-white dark:bg-background">
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-orange-500" />
            Zerar Estoque
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleFecharTemporada} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4 grid gap-4 content-start">
            <p className="text-sm text-muted-foreground">
              Esta ação irá salvar as quantidades atuais no histórico e zerar a contagem do estoque físico de <strong>todos</strong> os produtos. Essa operação não pode ser desfeita.
            </p>
            <div className="grid gap-2">
              <Label htmlFor="nomeTemporada">Nome da Temporada que está se encerrando</Label>
              <Input
                id="nomeTemporada"
                placeholder="Ex: Alta Temporada Janeiro 2026"
                value={nomeTemporada}
                onChange={(e) => setNomeTemporada(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0 bg-gray-50 dark:bg-card mt-auto gap-2 flex-col-reverse sm:flex-row">
            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} disabled={isClosing} className="w-full sm:w-auto">
              Cancelar
            </Button>
            <Button type="submit" variant="destructive" disabled={isClosing} className="w-full sm:w-auto">
              {isClosing ? "Processando..." : "Confirmar e Zerar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      </Dialog>

      {/* Modal de Reabertura de Temporada */}
      <Dialog open={isReopenModalOpen} onOpenChange={setIsReopenModalOpen}>
      <DialogContent aria-describedby={undefined} className="w-full h-[100dvh] max-h-none sm:h-auto sm:max-h-[85vh] sm:max-w-md p-0 border-0 sm:border rounded-none sm:rounded-lg overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b shrink-0 bg-white dark:bg-background">
          <DialogTitle className="flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-red-500" />
            Desfazer Fechamento
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleReabrirTemporada} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4 grid gap-4 content-start">
            <p className="text-sm text-muted-foreground">
              Você está prestes a <strong>desfazer</strong> o fechamento desta temporada. As quantidades que foram gravadas neste histórico voltarão para o estoque atual (ao vivo), e este registro de fechamento será apagado.
            </p>
            <p className="text-sm font-semibold text-red-600 dark:text-red-400">
              Atenção: Apenas faça isso se você não inseriu novas notas fiscais na temporada atual, senão as contagens serão sobrescritas e perdidas.
            </p>
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0 bg-gray-50 dark:bg-card mt-auto gap-2 flex-col-reverse sm:flex-row">
            <Button type="button" variant="outline" onClick={() => setIsReopenModalOpen(false)} disabled={isReopening} className="w-full sm:w-auto">
              Cancelar
            </Button>
            <Button type="submit" variant="destructive" disabled={isReopening} className="w-full sm:w-auto">
              {isReopening ? "Processando..." : "Desfazer Fechamento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListaEstoque;
